import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, Validators, NgForm } from '@angular/forms';

import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';

import { VersionsService } from '../../services/versions.service';
import { ApplicationVersion, ApplicationVersionReleaseNote } from '../../interfaces/applicationversion';
import { AlertsService } from '../../services/alerts.service';
import { Application } from '../../interfaces/application';
import { GlobalService } from '../../services/global.service';
import { MatDatepicker } from '@angular/material';
import { SessionToken } from '../../interfaces/sessiontoken';
import { TranslateService } from '@ngx-translate/core';
import { constants } from '../../../assets/constants';
import { AuthService } from '../../services/auth.service';
import { Gym } from '../../interfaces/gym';
import { ILanguageCountry, ILanguageResource } from '../../interfaces/language';
import { LanguageService } from '../../services/language.service';

@Component({
  selector: 'app-version-detail',
  templateUrl: './version-detail.component.html',
  styleUrls: ['./version-detail.component.scss']
})
export class VersionDetailComponent implements OnInit {
  @ViewChild(MatDatepicker, {static: false}) datepicker: MatDatepicker<Date>;

  canCreate: boolean;
  canModify: boolean;

  version_obj: ApplicationVersion = {
    id: null,
    application: null,
    version: '',
    creationDate: '',
    description: '',
    applyFrom: '',
    active: true,
    filename: '',
    gymsActive: [],
    dependencies: [],
    kioskStatus: [],
    daysToUpdate: 0,
    releaseNotes: [],
    beta: false
  };

  releaseNoteTypes: ILanguageCountry[] = [
    { 
      id: 1,
      name: "PRAMA"
    },
    { 
      id: 2,
      name: "Bootiq"
    },
  ];

  isNew: Boolean = false;
  appType: number;
  modalRef: BsModalRef;

  languages: ILanguageCountry[];
  applicationList: Application[];

  applicationVersions: ApplicationVersion[];

  selectedLanguage: ILanguageCountry;
  selectedType: ILanguageCountry;

  dependency: ApplicationVersion;

  currentReleaseNoteInput: ApplicationVersionReleaseNote;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private alertsService: AlertsService,
    private modalService: BsModalService,
    private globalService: GlobalService,
    private versionsService: VersionsService,
    private translate: TranslateService,
    private authService: AuthService,
    private languageService: LanguageService) {

    this.appType = 0;
  }

  ngOnInit() {
    this.languages = this.route.snapshot.data.languages;
    this.applicationList = this.route.snapshot.data.applicationList; 
    this.applicationVersions = this.route.snapshot.data.versionList.filter((appVersion: ApplicationVersion) => appVersion.application === 2).sort(({id:a}, {id:b}) => b-a);

    if (this.route.snapshot.data.version_obj === 'new') {
      this.isNew = true;

      this.version_obj.releaseNotes = this.languageService.createReleaseNotes(this.languages, this.releaseNoteTypes);

      this.translate.get('VERSIONS.TITLE_NEW').subscribe((translation) => {
        this.globalService.changePageTitle([translation]);
      });
    } else {
      this.version_obj = this.route.snapshot.data.version_obj;
      this.languageService.completeReleaseNotes(this.version_obj.releaseNotes, this.languages, this.releaseNoteTypes);

      if(this.version_obj.dependencies && this.version_obj.dependencies.length > 0) 
        this.dependency = this.version_obj.dependencies[0];

      this.globalService.changePageTitle(
        [
          this.globalService.getApplications().filter(app => (app.id === this.version_obj.application))[0].name,
          'v' + this.version_obj.version
        ]);
    }

    this.selectedLanguage = this.languages[0];
    this.selectedType = this.releaseNoteTypes[0];
    this.currentReleaseNoteInput = this.version_obj.releaseNotes.find(x => x.idLanguage == this.languages[0].id);

    this.canCreate = this.authService.isInRole(constants.roles.application_version_create);
    this.canModify = this.authService.isInRole(constants.roles.application_version_modify);

    this.versionChanged();
  }

  public saveVersion() {

    this.version_obj.dependencies = [];
    if(this.dependency)
      this.version_obj.dependencies.push(this.dependency);

    this.versionsService.putVersion(this.version_obj).subscribe(
      result => {
        this.translate.get('VERSIONS.VERSION_SAVED').subscribe((translation) => {
          this.alertsService.alertTrigger({ msg: translation, button: 'Ok' });
        });
      },
      error => {
        console.log('Error', error);
      }
    );
  }

  public createVersion() {

    this.version_obj.id = 0;
    const today = new Date();
    if (!this.version_obj.applyFrom) {
      this.version_obj.applyFrom = today.toISOString();
    }
    this.version_obj.creationDate = today.toISOString();

    this.version_obj.dependencies = [];
    if(this.dependency)
      this.version_obj.dependencies.push(this.dependency)

    this.versionsService.postVersion(this.version_obj).subscribe(
      result => {
        this.translate.get('VERSIONS.VERSION_CREATED').subscribe((translation) => {
          this.alertsService.alertTrigger({ type: 'success', msg: translation });
        });
      },
      error => {
        console.log('Error', error);
      }
    );
  }

  public deleteVersion() {
    this.versionsService.deleteVersion(this.version_obj.id).subscribe(
      result => {
        this.router.navigate(['/versions']);
        this.translate.get('VERSIONS.VERSION_DELETED').subscribe((translation) => {
          this.alertsService.alertTrigger({ type: 'success', msg: translation });
        });
      },
      error => {
        console.log('Error', error);
      }
    );
  }

  versionChanged() {
    if (this.applicationList) {
      const application = this.applicationList.filter(app => (app.id === this.version_obj.application));
      if (application[0] !== undefined) {
        this.appType = application[0].type;
      }
    }
  }

  private openDeleteModal(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template, { class: 'modal-sm modal-dialog-centered' });
  }

  private acceptDelete() {
    this.deleteVersion();
    this.modalRef.hide();
  }

  private cancelDelete() {
    this.modalRef.hide();
  }

  onSubmit() {
    if (this.isNew) {
      this.createVersion();
    } else {
      this.saveVersion();
    }
  }

  getReleaseNoteForLanguageAndType() {
    this.currentReleaseNoteInput = this.version_obj.releaseNotes.find(x => x.idLanguage== this.selectedLanguage.id && x.type == this.selectedType.id);
  }

  getLanguageReleaseNote() {

  }


  async postFile(data: File): Promise<any> {
    this.globalService.postFile(data).subscribe(
      fileName => {
        this.version_obj.filename = fileName;
      }, error => {
        this.translate.get('VERSIONS.ERROR_UPLOAD_VERSION').subscribe((translation) => {
          this.alertsService.alertTrigger({ msg: translation, button: 'Ok' });
        });
      }
    );
  }

   // Aux function for comparison of combobox objects
   compare(val1, val2) {
    if (val1 !== undefined && val2 !== undefined && val1 !== null && val2 !== null) {
      if(val1.id == val2.id) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }
}
